import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../css/Login.css';
import logo from '../assets/logo.png';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import authService from '../services/authService';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../i18n/translations';
import { ensureGoogleAdsTag, trackRegisterConversion } from '../services/trackingService';

const Login = () => {
  const [currentView, setCurrentView] = useState('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [country, setCountry] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [canResendVerification, setCanResendVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);

  const { login, register } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isRegister = currentView === 'register';

  const localizeAuthError = (rawMessage, fallbackKey) => {
    const message = String(rawMessage || '').trim();
    const mapped = {
      'Invalid credentials': t('login.errorInvalidCredentials'),
      'Credenziali non valide': t('login.errorInvalidCredentials'),
      'Registration failed': t('login.errorRegisterFailed'),
      'Registrazione non riuscita': t('login.errorRegisterFailed'),
      'User already exists': t('login.errorUserAlreadyExists'),
      'Company name is required': t('login.errorCompanyRequired'),
      'VAT number is required': t('login.errorVatRequired'),
      'Email non verificata. Controlla la casella di posta e clicca sul link di conferma.': t('login.errorEmailNotVerified'),
    };
    return mapped[message] || message || t(fallbackKey);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const requestedView = queryParams.get('view');
    const allowedViews = new Set(['login', 'register', 'forgotPassword', 'resetPassword']);

    if (requestedView && allowedViews.has(requestedView)) {
      setCurrentView(requestedView);
    }

    if (token) {
      setResetToken(token);
      setCurrentView('resetPassword');
    }
    if (queryParams.get('verified') === '1') {
      setFeedback(t('login.feedbackVerified'));
      setError('');
      setCurrentView('login');
    }
  }, [location.search, t]);

  useEffect(() => {
    if (currentView !== 'register') {
      return;
    }

    ensureGoogleAdsTag();

    const queryParams = new URLSearchParams(location.search);
    const requestedLang = queryParams.get('lang');
    const hasSavedPreference = Boolean(localStorage.getItem('app_language'));

    if (requestedLang || hasSavedPreference) {
      return;
    }

    const supportedLangs = Object.keys(translations);

    try {
      if (typeof navigator !== 'undefined') {
        const browserLocales = Array.isArray(navigator.languages) && navigator.languages.length > 0
          ? navigator.languages
          : [navigator.language];

        const normalized = browserLocales
          .filter(Boolean)
          .map((locale) => String(locale).toLowerCase());

        const matchedCode = supportedLangs.find((code) =>
          normalized.some((locale) => locale === code || locale.startsWith(`${code}-`))
        );

        if (matchedCode) {
          setLang(matchedCode);
          return;
        }
      }
    } catch (error) {
      console.warn('Cannot detect browser locale for register language auto switch', error);
    }

    let isItaly = false;
    try {
      const timezone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
      isItaly = timezone === 'Europe/Rome';
    } catch (error) {
      console.warn('Cannot detect timezone for register language auto switch', error);
    }

    setLang(isItaly ? 'it' : 'en');
  }, [currentView, location.search, setLang]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const switchView = (view) => {
    setError('');
    setFeedback('');
    setCanResendVerification(false);
    setCurrentView(view);
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError(t('login.errorForgotEmailRequired'));
      return;
    }

    try {
      setResendingVerification(true);
      setError('');
      await axios.post('/api/auth/resend-verification', { email });
      setFeedback(t('login.feedbackVerificationResent'));
      setCanResendVerification(true);
    } catch (resendError) {
      setError(t('login.errorVerificationResendFailed'));
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback('');
    try {
      if (currentView === 'register') {
        if (password !== confirmPassword) {
          setError(t('login.errorPasswordsMismatch'));
          return;
        }
        try {
          await register({ name: username, email, password, role, companyName, vatNumber, country, preferredLanguage: lang });
          trackRegisterConversion();
          setError('');
          setFeedback(t('login.feedbackRegisterDone'));
          setCurrentView('login');
          setCanResendVerification(true);
        } catch (regErr) {
          const msg = regErr?.response?.data?.message || regErr?.message || t('login.errorRegisterFailed');
          setError(localizeAuthError(msg, 'login.errorRegisterFailed'));
          return;
        }
      } else if (currentView === 'login') {
        try {
          await login({ email, password });
          navigate('/dashboard');
        } catch (loginError) {
          console.error('Login error:', loginError);
          const msg = loginError?.response?.data?.message || t('login.errorInvalidCredentials');
          setError(localizeAuthError(msg, 'login.errorInvalidCredentials'));
          if (loginError?.response?.data?.verificationRequired === true) {
            setCanResendVerification(true);
          } else {
            setCanResendVerification(false);
          }
        }
      } else if (currentView === 'forgotPassword') {
        if (!email) {
          setError(t('login.errorForgotEmailRequired'));
          return;
        }
        try {
          await axios.post('/api/password/request-password-reset', { email });
          setError('');
          setFeedback(t('login.feedbackForgotSent'));
        } catch (forgotError) {
          setError(t('login.errorForgotFailed'));
        }
      } else if (currentView === 'resetPassword') {
        if (newPassword.length < 8) {
          setError(t('login.errorMinPassword'));
          return;
        }
        if (newPassword !== confirmNewPassword) {
          setError(t('login.errorPasswordsMismatch'));
          return;
        }
        try {
          await authService.resetPassword(resetToken, newPassword);
          setError('');
          setFeedback(t('login.feedbackResetDone'));
          setTimeout(() => {
            setCurrentView('login');
            setError('');
            setFeedback('');
            navigate('/login');
          }, 3000);
        } catch (resetError) {
          setError(t('login.errorResetFailed'));
        }
      }
    } catch (err) {
      setError(t('login.errorGeneric'));
      console.error('Error during authentication:', err);
    }
  };

  return (
    <div className={`login-container ${isRegister ? 'register-mode' : ''}`}>
      <div className="login-left">
        {isRegister ? (
          <div className="register-landing">
            <img src={logo} alt={t('login.companyLogoAlt')} className="register-brand-logo" />
            <span className="register-badge">{t('login.landingBadge')}</span>
            <h1>{t('login.landingTitle')}</h1>
            <p>{t('login.landingSubtitle')}</p>
            <p className="landing-urgency">{t('login.landingUrgency')}</p>
            <ul className="register-benefits">
              <li>{t('login.landingBenefit1')}</li>
              <li>{t('login.landingBenefit2')}</li>
              <li>{t('login.landingBenefit3')}</li>
              <li>{t('login.landingBenefit4')}</li>
              <li>{t('login.landingBenefit5')}</li>
              <li>{t('login.landingBenefit6')}</li>
              <li>{t('login.landingBenefit7')}</li>
            </ul>
            <div className="register-trust">
              <span>{t('login.landingTrust1')}</span>
              <span>{t('login.landingTrust2')}</span>
              <span>{t('login.landingTrust3')}</span>
            </div>
            <div className="register-contact-card">
              <h3>{t('login.preferContactTitle')}</h3>
              <p>{t('login.preferContactText')}</p>
              <div className="register-contact-lines">
                <div>
                  <strong>{t('login.phoneLabel')}:</strong>{' '}
                  <a href="tel:+390434317912">+39 0434 317912</a>
                </div>
                <div>
                  <strong>{t('login.emailLabel')}:</strong>{' '}
                  <a href="mailto:info@gooooled.com">info@gooooled.com</a>
                </div>
              </div>
              <a className="register-contact-link" href="mailto:info@gooooled.com">
                {t('login.contactRequest')}
              </a>
            </div>
          </div>
        ) : (
          <img src={logo} alt={t('login.companyLogoAlt')} />
        )}
      </div>
      <div className="login-right">
        <div className={`login-box ${isRegister ? 'register-box' : ''}`}>
          <h2>
            {currentView === 'register'
              ? t('login.titleRegister')
              : currentView === 'forgotPassword'
                ? t('login.titleForgot')
                : currentView === 'resetPassword'
                  ? t('login.titleReset')
                  : t('login.titleLogin')}
          </h2>
          {isRegister && (
            <div className="register-form-note">
              <p className="register-form-note-title">{t('login.registerOnlyTitle')}</p>
              <p className="register-form-note-subtitle">{t('login.registerOnlySubtitle')}</p>
            </div>
          )}
          {feedback && <p className="feedback">{feedback}</p>}
          {error && <p className="error">{error}</p>}
          {currentView === 'login' && canResendVerification && (
            <div className="verification-help">
              <p>{t('login.checkSpamHint')}</p>
              <button
                type="button"
                className="auth-secondary-btn"
                onClick={handleResendVerification}
                disabled={resendingVerification}
              >
                {resendingVerification ? t('login.resendingVerification') : t('login.resendVerification')}
              </button>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            {currentView === 'register' && (
              <>
                <input
                  type="text"
                  placeholder={t('login.nameSurname')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
                <small className="field-hint">{t('login.hintLoginEmail')}</small>
                <input
                  type="text"
                  placeholder={t('login.company')}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                />
                <input
                  type="text"
                  placeholder={t('login.vatNumber')}
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  required
                />
                <select value={role} onChange={(e) => setRole(e.target.value)} required>
                  <option value="">{t('login.chooseRole')}</option>
                  <option value="user">{t('login.roleUser')}</option>
                  <option value="elettricista">{t('login.roleElectrician')}</option>
                  <option value="architetto">{t('login.roleArchitect')}</option>
                  <option value="azienda">{t('login.roleCompany')}</option>
                  <option value="agenzia">{t('login.roleAgency')}</option>
                  <option value="contractor">{t('login.roleContractor')}</option>
                </select>
                <select value={country} onChange={(e) => setCountry(e.target.value)} required>
                  <option value="">{t('account.country')}</option>
                  <option value="IT">{t('account.countryIt')}</option>
                  <option value="AT">{t('account.countryAt')}</option>
                  <option value="DE">{t('account.countryDe')}</option>
                  <option value="ES">{t('account.countryEs')}</option>
                  <option value="FR">{t('account.countryFr')}</option>
                  <option value="OTHER">{t('account.countryOther')}</option>
                </select>
              </>
            )}
            {(currentView === 'login' || currentView === 'register' || currentView === 'forgotPassword') && (
              <input
                type="email"
                placeholder={t('login.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            )}
            {(currentView === 'login' || currentView === 'register') && (
              <div className="password-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className="password-toggle" onClick={handleTogglePasswordVisibility}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            )}
            {currentView === 'register' && (
              <div className="password-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.confirmPassword')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            )}
            {currentView === 'forgotPassword' && <p>{t('login.forgotPrompt')}</p>}
            {currentView === 'resetPassword' && (
              <>
                <div className="password-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('login.newPassword')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <span className="password-toggle" onClick={handleTogglePasswordVisibility}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                <div className="password-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('login.confirmNewPassword')}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                  />
                </div>
              </>
            )}
            <button type="submit" className="auth-submit-btn">
              {currentView === 'register'
                ? t('login.submitRegister')
                : currentView === 'forgotPassword'
                  ? t('login.submitForgot')
                  : currentView === 'resetPassword'
                    ? t('login.submitReset')
                    : t('login.submitLogin')}
            </button>
          </form>

          {currentView === 'login' && (
            <>
              <button className="auth-secondary-btn" onClick={() => switchView('register')}>{t('login.noAccount')}</button>
              <div className="reset-password-link">
                <button className="auth-secondary-btn" onClick={() => switchView('forgotPassword')}>{t('login.forgotLink')}</button>
              </div>
            </>
          )}
          {currentView === 'register' && (
            <button className="auth-secondary-btn" onClick={() => switchView('login')}>{t('login.alreadyAccount')}</button>
          )}
          {(currentView === 'forgotPassword' || currentView === 'resetPassword') && (
            <button className="auth-secondary-btn" onClick={() => switchView('login')}>{t('login.backToLogin')}</button>
          )}
        </div>
        <div className="login-footer">
          <span>{"\u00A9 2026 GOOOLED srl"}</span>
          <span className="footer-divider">|</span>
          <Link to="/privacy" className="footer-link">
            {t('login.privacy')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
